import React,{useEffect,useRef,lazy,Suspense} from 'react';
import {Link,useNavigate} from 'react-router-dom';
import {ArrowRight,Mail,ShieldCheck,BadgeCheck,Award,Globe2,TrendingUp,Users,Car,Clock,Building2,Trophy,Plane,BedDouble,CalendarDays,Phone,Sparkles,Leaf,Handshake} from 'lucide-react';
import Seo,{orgJsonLd} from '../components/Seo';
import {Reveal,CountUp,MaskedHeading,FoldText,SpotlightCard,TrueFocus,ParticleText,Magnetic,fireSpark,useReducedMotion,useIsDesktop,useIsMobile} from '../components/effects';
import {companyFacts,company} from '../data/company';
import {pillars,ctmPillars,flowSteps,finalWords} from '../data/history';
import {approvedReferences,accreditations} from '../data/portfolio';
import {homeServices} from '../data/services';

const IMG={
  human:'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200&q=80'
};

/* cursor-follow spotlight glow for cards */
function glowMove(e){
  const el=e.currentTarget;const r=el.getBoundingClientRect();
  el.style.setProperty('--mx',((e.clientX-r.left)/r.width*100).toFixed(1)+'%');
  el.style.setProperty('--my',((e.clientY-r.top)/r.height*100).toFixed(1)+'%');
}

/* WebGL globe is code-split so Three.js/R3F stays out of the initial bundle (perf). */
const HeroGlobeCanvas=lazy(()=>import('../components/HeroGlobe'));

/* ============ OPENING ORANGE POINT (origin of the route) ============ */
const ROUTE_STOPS=[
  {x:820,y:150,label:'Uçak Bileti',ly:104,v:'--n1',to:'/ucak-bileti',Icon:Plane},
  {x:930,y:300,label:'Konaklama',ly:352,v:'--n2',to:'/otel-konaklama',Icon:BedDouble},
  {x:1040,y:160,label:'Vize Hizmetleri',ly:114,v:'--n3',to:'/vize-hizmetleri',Icon:ShieldCheck},
  {x:1150,y:300,label:'Araç Kiralama',ly:352,v:'--n4',to:'/arac-kiralama',Icon:Car},
  {x:1260,y:170,label:'Etkinlik / MICE',ly:124,v:'--n5',to:'/etkinlik-organizasyon',Icon:CalendarDays},
  {x:1360,y:290,label:'VIP Transfer',ly:342,v:'--n6',to:'/vip-transfer',Icon:Sparkles}
];
function PointIntro(){
  const rigRef=useRef(null);const stageRef=useRef(null);const reduced=useReducedMotion();
  const isDesktop=useIsDesktop();const isMobile=useIsMobile();
  const staticMode=reduced||isMobile;
  const navigate=useNavigate();
  const [showGlobe,setShowGlobe]=React.useState(true);const globeOnRef=useRef(true);const collapseRef=useRef(0);
  const [globeReady,setGlobeReady]=React.useState(false);
  useEffect(()=>{
    if(staticMode)return;
    const ric=window.requestIdleCallback||((cb)=>setTimeout(cb,300));
    const cancel=window.cancelIdleCallback||clearTimeout;
    const id=ric(()=>setGlobeReady(true),{timeout:1200});
    return()=>cancel(id);
  },[staticMode]);
  useEffect(()=>{
    if(staticMode){setShowGlobe(false);return;}
    const rig=rigRef.current;const stage=stageRef.current;if(!rig||!stage)return;
    const S=stage.style;let ticking=false,prev=-1;
    let total=Math.max(0,rig.offsetHeight-window.innerHeight);
    const clamp=(v,a=0,b=1)=>Math.min(b,Math.max(a,v));
    const ss=(e0,e1,v)=>{const x=clamp((v-e0)/(e1-e0));return x*x*(3-2*x);};
    const compute=()=>{
      ticking=false;
      const r=rig.getBoundingClientRect();
      const off=r.bottom<-240||r.top>window.innerHeight+240;
      if(off){if(globeOnRef.current){globeOnRef.current=false;setShowGlobe(false);}return;}
      const cur=total>0?clamp(-r.top/total):0;
      collapseRef.current=clamp((cur-0.12)/0.26);
      const wantGlobe=cur<0.55;
      if(wantGlobe!==globeOnRef.current){globeOnRef.current=wantGlobe;setShowGlobe(wantGlobe);}
      if(Math.abs(cur-prev)<0.0004)return;
      prev=cur;
      // ---- globe hero phase: globe collapses (eased on GPU) into the orange point ----
      S.setProperty('--copy-op',(1-ss(0.10,0.30,cur)).toFixed(3));
      S.setProperty('--copy-y',`${(-ss(0.10,0.42,cur)*44).toFixed(1)}px`);
      S.setProperty('--globe-op',(1-ss(0.40,0.52,cur)).toFixed(3));
      S.setProperty('--pidot-op',ss(0.40,0.52,cur).toFixed(3));
      // ---- route phase (remap cur 0.52 -> 1) : Uçak, Konaklama... ----
      const rp=clamp((cur-0.52)/0.48);
      S.setProperty('--p',rp.toFixed(4));
      const panMax=isDesktop?-30:-58;
      S.setProperty('--pan',`${(ss(0.04,0.82,rp)*panMax).toFixed(3)}vw`);
      S.setProperty('--dot-scale',(1+rp*1.2).toFixed(3));
      S.setProperty('--line',ss(0.04,0.80,rp).toFixed(4));
      S.setProperty('--n1',ss(0.08,0.20,rp).toFixed(3));
      S.setProperty('--n2',ss(0.20,0.32,rp).toFixed(3));
      S.setProperty('--n3',ss(0.32,0.44,rp).toFixed(3));
      S.setProperty('--n4',ss(0.44,0.56,rp).toFixed(3));
      S.setProperty('--n5',ss(0.56,0.68,rp).toFixed(3));
      S.setProperty('--n6',ss(0.68,0.80,rp).toFixed(3));
      S.setProperty('--tag-op',ss(0.10,0.24,rp).toFixed(3));
      S.setProperty('--cue-op',(1-ss(0.02,0.12,cur)).toFixed(3));
    };
    const onScroll=()=>{if(!ticking){ticking=true;requestAnimationFrame(compute);}};
    const onResize=()=>{total=Math.max(0,rig.offsetHeight-window.innerHeight);onScroll();};
    window.addEventListener('scroll',onScroll,{passive:true});
    window.addEventListener('resize',onResize);
    compute();
    return()=>{window.removeEventListener('scroll',onScroll);window.removeEventListener('resize',onResize);};
  },[staticMode,isMobile,reduced,isDesktop]);
  return <section className={`pi-rig${staticMode?' pi-reduced':''}`} ref={rigRef} aria-label="Giriş — başlangıç noktası">
    <div className="pi-stage" ref={stageRef}>
      {showGlobe&&globeReady&&<div className="pi-globe" style={{opacity:'var(--globe-op,1)'}}>
        <Suspense fallback={null}>
          <HeroGlobeCanvas collapse={collapseRef} centered={isMobile}/>
        </Suspense>
      </div>}
      <div className="gh-copy pi-hero-copy">
        <span className="eyebrow">PLAZA TURİZM</span>
        <h2 className="h2">Kurumsal Çözümlerde <span className="hl-dogru">Doğru</span> Nokta.</h2>
        <p className="body-txt">Kurumsal seyahat yönetiminde en yeni teknolojileri kullanarak müşterilerimizin iş süreçlerini kısaltıyor, maliyetlerini düşürüyor ve memnuniyeti artırmak için kendimizi sürekli yeniliyoruz.</p>
      </div>
      <div className="pi-inner" style={{opacity:'var(--pi-fade,1)'}}>
        <svg className="pi-route" viewBox="0 0 1400 460" fill="none" aria-hidden="true" preserveAspectRatio="xMidYMid meet">
          <path className="pi-path" pathLength="1" strokeLinejoin="round" d="M700 230 C 750 190 790 150 820 150 C 860 150 890 300 930 300 C 980 300 1000 160 1040 160 C 1090 160 1110 300 1150 300 C 1200 300 1220 170 1260 170 C 1310 170 1330 290 1360 290" stroke="var(--plaza-orange)" strokeWidth="3" strokeLinecap="round"/>
          {ROUTE_STOPS.map(s=>(
            <g key={s.label} className="pi-stop" style={{opacity:`var(${s.v},0)`}} role="link" tabIndex={0} aria-label={s.label} data-cursor="go"
               onClick={()=>navigate(s.to)} onKeyDown={(e)=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();navigate(s.to);}}}>
              <circle className="pi-hit" cx={s.x} cy={s.y} r="34"/>
              <circle className="pi-node-halo" cx={s.x} cy={s.y} r="24"/>
              <circle className="pi-node-chip" cx={s.x} cy={s.y} r="17"/>
              <foreignObject x={s.x-11} y={s.y-11} width="22" height="22" style={{pointerEvents:'none'}}>
                <div className="pi-node-ic"><s.Icon size={18} strokeWidth={2.2} color="#F7620A"/></div>
              </foreignObject>
              <text className="pi-label" x={s.x} y={s.ly} textAnchor="middle">{s.label}</text>
            </g>
          ))}
        </svg>
        <div className="pi-mstops" aria-label="Hizmet rotası">
          {ROUTE_STOPS.map(s=>(
            <Link key={s.label} to={s.to} className="pi-mstop" aria-label={s.label} data-testid={`pi-mstop-${s.to.slice(1)}`}>
              <span className="pi-mstop-ic"><s.Icon size={20} strokeWidth={2.2}/></span>
              <span className="pi-mstop-label">{s.label}</span>
              <ArrowRight size={17} className="pi-mstop-arrow"/>
            </Link>
          ))}
        </div>
        <span className="pi-dot" style={{opacity:'var(--pidot-op,1)'}} aria-hidden="true"/>
        <div className="pi-tag">
          <span className="eyebrow heartbeat-eyebrow">1992'DEN BERİ</span>
          <h2 className="h2">Her yolculuk <span className="hl-dogru">doğru</span> bir noktadan başlar.</h2>
        </div>
      </div>
      <div className="oc-scrollcue pi-cue"><span>KAYDIR</span><span className="bar"/></div>
    </div>
  </section>;
}

/* ============ SIGHTS SLIDER (Mostar-style horizontal scrub) ============ */
function SightsSlider(){
  const rigRef=useRef(null);const trackRef=useRef(null);
  const reduced=useReducedMotion();const isDesktop=useIsDesktop();
  const scrub=isDesktop&&!reduced;
  useEffect(()=>{
    if(!scrub)return;
    const rig=rigRef.current;const track=trackRef.current;if(!rig||!track)return;
    const T=track.style;let raf=null,running=false,cur=0,init=false,last=0,prev=-1;
    const clamp=(v,a=0,b=1)=>Math.min(b,Math.max(a,v));
    const TAU=0.05;   // short => smooth wheel glide, still responsive on touchpad/mobile
    let total=Math.max(0,rig.offsetHeight-window.innerHeight);
    let maxX=Math.max(0,track.scrollWidth-track.parentElement.clientWidth);
    const frame=(t)=>{
      const dt=last?Math.min(0.05,(t-last)/1000):0.016;last=t;
      const r=rig.getBoundingClientRect();
      if(r.bottom<-240||r.top>window.innerHeight+240){running=false;raf=null;last=0;return;}
      const target=total>0?clamp(-r.top/total):0;
      const k=1-Math.exp(-dt/TAU);
      if(!init){cur=target;init=true;}else cur+=(target-cur)*k;
      if(Math.abs(target-cur)<0.00012)cur=target;
      if(Math.abs(cur-prev)>=0.0004){
        prev=cur;
        T.transform=`translate3d(${(-cur*maxX).toFixed(2)}px,0,0)`;
        rig.style.setProperty('--sp',cur.toFixed(4));
      }
      if(cur!==target){raf=requestAnimationFrame(frame);}else{running=false;raf=null;last=0;}
    };
    const start=()=>{if(!running){total=Math.max(0,rig.offsetHeight-window.innerHeight);maxX=Math.max(0,track.scrollWidth-track.parentElement.clientWidth);running=true;last=0;raf=requestAnimationFrame(frame);}};
    const onScroll=()=>start();
    const onResize=()=>{total=Math.max(0,rig.offsetHeight-window.innerHeight);maxX=Math.max(0,track.scrollWidth-track.parentElement.clientWidth);start();};
    window.addEventListener('scroll',onScroll,{passive:true});
    window.addEventListener('resize',onResize);
    start();
    return()=>{running=false;if(raf)cancelAnimationFrame(raf);window.removeEventListener('scroll',onScroll);window.removeEventListener('resize',onResize);};
  },[scrub]);
  const cards=(
    <div className="sights-track" ref={trackRef}>
      <div className="sights-lead">
        <span className="eyebrow">HİZMET ROTASI</span>
        <h2 className="h2">Uçtan uca <span className="hl-dogru">kurumsal</span> seyahat operasyonu</h2>
        <p className="body-txt" style={{marginTop:14}}>7/24 canlı destek uzmanımız, 280'den fazla yerel ve global partnerimiz ve dahası!</p>
      </div>
      {homeServices.map((s,i)=>(
        <Link key={s.slug} to={`/${s.slug}`} className="sight-card" data-cursor="discover" aria-label={s.title}>
          <div className="sight-img" style={{backgroundImage:`url(${s.img})`}}/>
          <span className="sight-num">{s.n}</span>
          <div className="sight-body">
            <h3>{s.title}</h3>
            <p>{s.line}</p>
            <span className="sight-go">Keşfet <ArrowRight size={16} className="arrow"/></span>
          </div>
        </Link>
      ))}
      <Link to="/iletisim" className="sight-card sight-card-cta" data-cursor="go" onClick={(e)=>fireSpark(e.clientX,e.clientY)}>
        <div className="sight-body">
          <span className="sight-dot"/>
          <h3>Rotanızı birlikte çizelim.</h3>
          <p>Kurumsal seyahat operasyonunuzu tek noktadan yönetelim.</p>
          <span className="sight-go">İletişime Geç <ArrowRight size={16} className="arrow"/></span>
        </div>
      </Link>
    </div>
  );
  if(!scrub){
    return <section className="sights-rig sights-static" aria-label="Hizmet rotası">
      <div className="sights-viewport sights-scroll">{cards}</div>
    </section>;
  }
  return <section className="sights-rig" ref={rigRef} aria-label="Hizmet rotası">
    <div className="sights-sticky">
      <div className="sights-viewport">{cards}</div>
      <div className="sights-progress"><span className="sights-progress-fill"/><span className="sights-progress-dot"/></div>
    </div>
  </section>;
}

/* ============ ACHIEVEMENTS (horizontal scrub, same living feel) ============ */
const ACHIEVEMENTS=[
  {type:'stat',n:'34',label:'Yıllık Deneyim',Icon:Clock},
  {type:'stat',n:'600+',label:'Kurumsal Müşteri',Icon:Handshake},
  {type:'stat',n:'4',label:'Ülke & Ofis',Icon:Building2},
  {type:'stat',n:'84',label:'Kişilik 7/24 Profesyonel Kadro',Icon:Users},
  {type:'stat',n:'24',label:'Araçlık VIP Araç Filosu',Icon:Car},
  {type:'award',title:'ACE OF M.I.C.E.',desc:'En İyi Kongre Organizatörü & En İyi Etkinlik Yönetim Firması',Icon:Award,to:'/plazette/ace-of-mice-kurumsal-seyahat-yonetimi'},
  {type:'award',title:'Ekonomiye Değer Katanlar',desc:'Turizm Sektörü Lider Firması',Icon:TrendingUp,to:'/plazette/btso-ekonomiye-deger-katanlar-plaza-turizm-sektor-lideri'},
  {type:'award',title:'The Global Compact',desc:'BM Küresel İlkeler Sözleşmesi imzacısı ve üyesi',Icon:Globe2},
  {type:'award',title:'TFF Dünya Kupası',desc:'Türk Milli Takımı ve Türkiye Futbol Federasyonu 2002 Dünya Kupası Organizasyonu',Icon:Trophy},
  {type:'award',title:'TFF Avrupa Şampiyonası',desc:'Türk Milli Takımı ve Türkiye Futbol Federasyonu 2008 Avrupa Şampiyonası Organizasyonu',Icon:Trophy},
  {type:'award',title:'UEFA Şampiyonlar Ligi',desc:'UEFA Şampiyonlar Ligi Finali Organizasyonu',Icon:Trophy},
  {type:'cert',title:'T.C. Turizm Bakanlığı',desc:'"A" Grubu Belgesi · ISO 9001:2000',Icon:BadgeCheck},
  {type:'cert',title:'Uluslararası Üyelikler',desc:'TÜRSAB · ASTA · ICCA · IATA · LCC',Icon:ShieldCheck}
];
const ACCREDITATIONS=[
  {n:'The Global Compact',l:'global-compact'},{n:'TÜRSAB',l:'tursab'},{n:'KalDer',l:'kalder'},
  {n:'ISO 9001:2000',l:'iso9001'},{n:'ATPI',l:'atpi'},{n:'Türk Hava Yolları',l:'turkish-airlines'},
  {n:'Lufthansa City Center',l:'lufthansa'},{n:'ICVB',l:'icvb'},{n:'SEDEX',l:'sedex'},{n:'BTSO',l:'btso'},
  {n:'ICCA',l:'icca'},{n:'ASTA',l:'asta'},{n:'Sixt',l:'sixt'},{n:'Avis',l:'avis'},{n:'D2',l:'d2'},
  {n:'AXA Sigorta',l:'axa'},{n:'SKAL',l:'skal'}
];
const LOGO_DIMS={
  'global-compact':[240,86],'tursab':[240,86],'kalder':[209,90],'iso9001':[117,90],
  'atpi':[100,90],'turkish-airlines':[239,75],'lufthansa':[240,29],'icvb':[228,80],
  'sedex':[89,90],'btso':[235,79],'icca':[89,90],'asta':[162,90],'sixt':[190,85],
  'avis':[194,82],'d2':[102,90],'axa':[240,88],'skal':[240,84]
};

function AchievementsSlider(){
  const rigRef=useRef(null);const planeRef=useRef(null);
  const reduced=useReducedMotion();const isDesktop=useIsDesktop();
  const on=isDesktop&&!reduced;
  useEffect(()=>{
    if(!on)return;
    const rig=rigRef.current;const plane=planeRef.current;if(!rig||!plane)return;
    let raf=null,running=false,cur=0,init=false,last=0,prev=-1;
    const clamp=(v,a=0,b=1)=>Math.min(b,Math.max(a,v));
    const TAU=0.06;
    let total=Math.max(0,rig.offsetHeight-window.innerHeight);
    let planeH=plane.offsetHeight;
    const frame=(t)=>{
      const dt=last?Math.min(0.05,(t-last)/1000):0.016;last=t;
      const r=rig.getBoundingClientRect();
      if(r.bottom<-240||r.top>window.innerHeight+240){running=false;raf=null;last=0;return;}
      const target=total>0?clamp(-r.top/total):0;
      const k=1-Math.exp(-dt/TAU);
      if(!init){cur=target;init=true;}else cur+=(target-cur)*k;
      if(Math.abs(target-cur)<0.00012)cur=target;
      if(Math.abs(cur-prev)>=0.0004){
        prev=cur;
        const vh=window.innerHeight;
        const start=vh*0.25+planeH/2;
        const travel=planeH-vh*0.10;
        const ty=start-cur*travel;
        plane.style.transform=`rotateX(42deg) translateY(${ty.toFixed(1)}px)`;
      }
      if(cur!==target){raf=requestAnimationFrame(frame);}else{running=false;raf=null;last=0;}
    };
    const startLoop=()=>{if(!running){total=Math.max(0,rig.offsetHeight-window.innerHeight);planeH=plane.offsetHeight;running=true;last=0;raf=requestAnimationFrame(frame);}};
    const onScroll=()=>startLoop();
    const onResize=()=>{total=Math.max(0,rig.offsetHeight-window.innerHeight);planeH=plane.offsetHeight;startLoop();};
    window.addEventListener('scroll',onScroll,{passive:true});
    window.addEventListener('resize',onResize);
    startLoop();
    return()=>{running=false;if(raf)cancelAnimationFrame(raf);window.removeEventListener('scroll',onScroll);window.removeEventListener('resize',onResize);};
  },[on]);
  const renderRow=(a,i)=>{
    if(a.type==='wide') return <div key="wide" className="ach3d-row ach3d-wide">
      <span className="ach3d-ic"><BadgeCheck size={22} strokeWidth={2.1}/></span>
      <div><h3>Üyelikler & Akreditasyonlar</h3>
        <div className="ach3d-chips">{ACCREDITATIONS.map(c=><span key={c.n} className="ach3d-chip"><img className="ach3d-logo" src={`/assets/logos/${c.l}.png`} alt={c.n} loading="lazy" width={LOGO_DIMS[c.l][0]} height={LOGO_DIMS[c.l][1]}/><span>{c.n}</span></span>)}</div></div>
    </div>;
    return <div key={i} className={`ach3d-row ach3d-${a.type}`}>
      <span className="ach3d-ic"><a.Icon size={22} strokeWidth={2.1}/></span>
      {a.type==='stat'
        ? <div className="ach3d-stat"><span className="ach3d-num">{a.n}</span><span className="ach3d-lbl">{a.label}</span></div>
        : (a.to
            ? <Link to={a.to} className="ach3d-link" data-cursor="discover" data-testid={`ach-link-${i}`}><h3>{a.title} <ArrowRight size={16} className="ach3d-arrow"/></h3><p>{a.desc}</p></Link>
            : <div><h3>{a.title}</h3><p>{a.desc}</p></div>)}
    </div>;
  };
  const rows=[...ACHIEVEMENTS,{type:'wide'}];
  const head=<div className="ach3d-head"><span className="eyebrow">BAŞARILAR</span><h2 className="h2"><span className="hl-dogru">Güven</span> söylenmez, belgelenir.</h2></div>;
  if(!on){
    return <section className="ach3d-rig ach3d-static" aria-label="Başarılar ve akreditasyonlar">
      {head}<div className="ach3d-stack">{rows.map(renderRow)}</div>
    </section>;
  }
  return <section className="ach3d-rig" ref={rigRef} aria-label="Başarılar ve akreditasyonlar">
    <div className="ach3d-stage">
      {head}
      <div className="ach3d-plane" ref={planeRef}>{rows.map(renderRow)}</div>
      <div className="ach3d-fade"/>
    </div>
  </section>;
}


/* ============ PARTNERS SPLIT (animated two-panel) ============ */
function PartnersSplit(){
  const ref=useRef(null);const [inview,setInview]=React.useState(false);
  useEffect(()=>{
    const el=ref.current;if(!el)return;
    const io=new IntersectionObserver((es)=>es.forEach(e=>setInview(e.isIntersecting)),{threshold:0.2});
    io.observe(el);return()=>io.disconnect();
  },[]);
  const P=[
    {cls:'psplit-a',to:'/atpi',brand:'ATPI · Direct Travel',title:'ATPI (Advanced Travel Partners) Türkiye Tek Yetkililiği',desc:'Dünyanın en büyük kurumsal seyahat ve etkinlik çözümleri yönetim organizasyonu ATPI\u2019nin Türkiye tek temsilcisi.',img:'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1400&q=80'},
    {cls:'psplit-b',to:'/lufthansa-city-center',brand:'Lufthansa City Center',title:'Lufthansa City Center Yetkili Ofisi',desc:'Dünya çapında 105 ülkede 561 acente ile hizmet ağı olan Lufthansa Yetkili Acenteliği ile global avantajlardan siz de yararlanın.',img:'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1400&q=80'}
  ];
  return <section ref={ref} className={`partners-split${inview?' in':''}`} aria-label="Yetkili ofis ve global temsilcilik">
    {P.map((p)=>(
      <Link key={p.cls} to={p.to} className={`psplit ${p.cls}`} data-cursor="discover" data-testid={`partner-card-${p.to.slice(1)}`}>
        <div className="psplit-media" style={{backgroundImage:`url(${p.img})`}}/>
        <span className="psplit-brand">{p.brand}</span>
        <div className="psplit-body">
          <h3>{p.title}</h3>
          <p>{p.desc}</p>
          <span className="psplit-cta">Detaylı bilgi <ArrowRight size={17} className="arrow"/></span>
        </div>
      </Link>
    ))}
  </section>;
}


/* ============ CARBON BAND ============ */
function YtEmbed({id,title}){
  const [play,setPlay]=React.useState(false);
  const thumb=`https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
  return <div className="yt-embed" data-testid="carbon-video">
    {play
      ? <iframe src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`} title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy"/>
      : <button type="button" className="yt-facade" onClick={()=>setPlay(true)} aria-label={`${title} videosunu oynat`} style={{backgroundImage:`url(${thumb})`}}>
          <span className="yt-play"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg></span>
        </button>}
  </div>;
}

function CarbonBand(){
  const ref=useRef(null);const [inview,setInview]=React.useState(false);
  useEffect(()=>{
    const el=ref.current;if(!el)return;
    const io=new IntersectionObserver((es)=>es.forEach(e=>{if(e.isIntersecting)setInview(true);}),{threshold:0.35});
    io.observe(el);return()=>io.disconnect();
  },[]);
  return <section ref={ref} className={`carbon-band${inview?' in':''}`} aria-label="Sürdürülebilir kurumsal seyahat ve karbon ayak izi">
    <span className="carbon-leaf cl1"><Leaf size={30}/></span>
    <span className="carbon-leaf cl2"><Leaf size={20}/></span>
    <span className="carbon-leaf cl3"><Leaf size={26}/></span>
    <span className="carbon-leaf cl4"><Leaf size={18}/></span>
    <span className="carbon-leaf cl5"><Leaf size={22}/></span>
    <span className="carbon-leaf cl6"><Leaf size={16}/></span>
    <div className="container">
      <div className="carbon-card" data-testid="carbon-band">
        <span className="carbon-badge"><Leaf size={30} strokeWidth={2.1}/></span>
        <span className="eyebrow carbon-eyebrow">BİLİYOR MUYDUNUZ?</span>
        <h2 className="h2 carbon-title">Seyahatinizin <span className="hl-green">karbon ayak izini</span> ölçüyor ve azaltıyoruz.</h2>
        <p className="body-txt carbon-text">Uçuş, konaklama ve dahası kaynaklı karbon emisyonlarını detaylı şekilde analiz ediyor; sürdürülebilir kurumsal seyahat politikaları gereği karbon ayak izinizi raporluyor ve azaltıyoruz.</p>
        <YtEmbed id="skFYFfF34cs" title="Plaza Turizm ile Karbon Salınımına Karşı Sürdürülebilir Adımlar"/>
      </div>
    </div>
  </section>;
}

/* ============ CONTINUATION SCENES ============ */
function Scene({id,className='',tall,img,children}){
  return <section id={id} className={`scene ${className}`} style={tall?{minHeight:tall}:undefined}>
    {img&&<div className="scene-vis" style={{backgroundImage:`url(${img})`}} aria-hidden="true"/>}
    <div className="container scene-inner">{children}</div>
  </section>;
}

function AtpiBand(){
  return <section className="atpi-band" data-testid="atpi-band">
    <div className="atpi-band-inner">
      <Reveal><span className="atpi-eyebrow"><Globe2 size={15}/> ULUSLARARASI TMC AĞI</span></Reveal>
      <Reveal delay={1}><h2 className="atpi-title">ATPI Global Network — <span className="atpi-hl">Türkiye'nin Tek Yetkili Temsilcisi</span></h2></Reveal>
      <Reveal delay={2}><p className="atpi-sub">Güçlü yerel operasyonumuz, uluslararası TMC gücüyle birleşiyor: 100+ ülkede global satın alma gücü, kurumsal teknoloji, 7/24 operasyon ve duty of care.</p></Reveal>
      <Reveal delay={3}><div className="atpi-chips">
        <span><Globe2 size={15}/>100+ Ülke</span>
        <span><TrendingUp size={15}/>Global Satın Alma Gücü</span>
        <span><ShieldCheck size={15}/>7/24 Duty of Care</span>
        <span><Sparkles size={15}/>Kurumsal Teknoloji</span>
      </div></Reveal>
      <Reveal delay={4}><Link to="/atpi" className="btn btn-primary atpi-cta" data-cursor="go" data-testid="atpi-band-cta">ATPI Gücünü Keşfedin <ArrowRight size={16}/></Link></Reveal>
    </div>
  </section>;
}

export default function Home(){
  const reduced=useReducedMotion();
  const finalRef=useRef(null);const [pulled,setPulled]=React.useState(false);
  useEffect(()=>{const o=new IntersectionObserver(([e])=>e.isIntersecting&&setPulled(true),{threshold:.35});if(finalRef.current)o.observe(finalRef.current);return()=>o.disconnect();},[]);
  return <div className="cine-home">
    <Seo title="Plaza Turizm | Kurumsal Seyahat Yönetimi" description="1992'den beri uçuş, otel, VIP transfer, araç kiralama, vize, etkinlik ve kurumsal seyahat yönetimi çözümleri. Plaza Turizm ile seyahat operasyonunuzu tek noktadan yönetin." path="/" jsonLd={orgJsonLd()}/>

    <h1 className="sr-only">Plaza Turizm — Kurumsal Seyahat Yönetimi, VIP Transfer, Vize ve Etkinlik Operasyonu</h1>
    <PointIntro/>

    <div className="cine-content">
      {/* HİZMET ROTASI — horizontal service slider */}
      <SightsSlider/>
      {/* BAŞARILAR — achievements & accreditations horizontal scrub */}
      <AchievementsSlider/>
      {/* PARTNERS SPLIT — Lufthansa & ATPI */}
      <PartnersSplit/>
      {/* CARBON / SUSTAINABILITY BAND */}
      <CarbonBand/>
      {/* HUMAN */}
      <Scene id="human" className="right-scene" tall="auto" img={IMG.human}>
        <div className="scene-text">
          <span className="eyebrow">İNSAN UZMANLIĞI</span>
          <MaskedHeading className="h2">Teknoloji hızlandırır.</MaskedHeading>
          <FoldText className="h2"><span className="hl-dogru">İnsan çözer.</span></FoldText>
          <p className="body-txt" style={{marginTop:14}}>Sistemler süreçleri hızlandırır. Fark yaratan ise deneyim, dikkat ve insan uzmanlığıdır.</p>
          <div style={{marginTop:26}}><TrueFocus words={pillars}/></div>
        </div>
      </Scene>
      {/* PLAZA FLOW */}
      <Scene id="flow" className="flow-scene" tall="auto">
        <div style={{textAlign:'center'}}>
          <Reveal><p className="eyebrow" style={{marginBottom:4}}>PEKİ BÜTÜN BUNLARI NASIL YÖNETİYORUZ?</p></Reveal>
          <div style={{position:'relative',minHeight:210}}><ParticleText text="PLAZA FLOW" height={210}/></div>
          <Reveal><p className="body-txt" style={{margin:'0 auto'}}>Plaza Flow, kurumsal seyahat operasyonlarımızın dijital omurgasıdır. Yapay zekâ destekli altyapısı sayesinde talepleri saniyeler içinde analiz eder, doğru ekiplere yönlendirir, süreçleri otomatikleştirir ve SLA'dan raporlamaya kadar tüm operasyonu uçtan uca görünür ve yönetilebilir hale getirir.</p></Reveal>
        </div>
        <div style={{marginTop:56}}>
          <Reveal><div className="email-row">
            <div className="email-card" onMouseMove={glowMove}><div className="from"><Plane size={13} style={{display:'inline',marginRight:6,verticalAlign:'-2px'}}/>Uçuş Talebi</div><p>"Yarın Frankfurt'ta toplantımız var. Sabah uçuş alternatiflerini paylaşabilir misiniz?"</p></div>
            <div className="email-card" onMouseMove={glowMove}><div className="from"><BedDouble size={13} style={{display:'inline',marginRight:6,verticalAlign:'-2px'}}/>Konaklama Talebi</div><p>"Ekibimiz için 3 gece şehir merkezinde otel rezervasyonu yapabilir misiniz?"</p></div>
            <div className="email-card" onMouseMove={glowMove}><div className="from"><CalendarDays size={13} style={{display:'inline',marginRight:6,verticalAlign:'-2px'}}/>Etkinlik Talebi</div><p>"200 kişilik yıllık bayi toplantımızın organizasyonunu planlayabilir misiniz?"</p></div>
          </div></Reveal>
          <div className="flow">{flowSteps.map((s,i)=>(<Reveal key={i} delay={(i%4)+1} className="flow-node"><div className="glow-card"><div className="fn">{s.n}</div><p className="fn-title">{s.t}</p><p>{s.d}</p></div></Reveal>))}</div>
        </div>
      </Scene>
      {/* FINAL */}
      <section id="final" className="scene final-scene" ref={finalRef} style={{minHeight:'100svh'}}>
        <div className="container final-inner">
          <div className="final-words">
            {finalWords.map((w,i)=>{const ang=(i/finalWords.length)*Math.PI*2;return <span key={i} className={`final-word${(pulled&&!reduced)?' pulled':''}`} style={{left:`${50+Math.cos(ang)*34}%`,top:`${50+Math.sin(ang)*30}%`,transitionDelay:`${i*0.04}s`}}>{w}</span>;})}
            <span className="final-point"/>
          </div>
          <h2 className="h2">Dünyanın neresine giderseniz gidin,<br/>kurumsal seyahatiniz <span className="hl-dogru">tek bir noktadan</span> yönetilsin.</h2>
          <img src="/assets/plaza-logo.png" alt="Plaza Turizm" className="final-logo" width="399" height="97"/>
          <p className="small" style={{marginBottom:28}}>Kurumsal Çözümlerde Doğru Nokta.</p>
          <div className="final-contact">
            <a href={company.phoneHref} className="final-contact-item" data-cursor="go" onMouseMove={glowMove}><Phone size={22} strokeWidth={2.1}/><span><small>Telefon</small><b>{company.phone}</b></span></a>
            <a href={`mailto:${company.email}`} className="final-contact-item" data-cursor="go" onMouseMove={glowMove}><Mail size={22} strokeWidth={2.1}/><span><small>E-posta</small><b>{company.email}</b></span></a>
          </div>
        </div>
      </section>
    </div>
  </div>;
}
